import React from 'react';
import {
  Users,
  Target,
  Award,
  Shield,
  Zap,
  TrendingUp,
  BarChart2,
  BrainCircuit,
  LineChart,
  GraduationCap,
  Timer,
  Fingerprint,
  Layers,
  CalendarRange,
  MessageCircle,
  BookMarked,
  Video,
  MapPin,
  MessagesSquare,
} from 'lucide-react';

import SeoMeta from './SeoMeta';
const NavyDivider = () => (
  <div className="w-16 h-px bg-navy-200 mx-auto mb-4" />
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-center gap-3 mb-4">
    <div className="w-8 h-px bg-navy-200" />
    <span className="text-navy-500 text-xs tracking-[0.25em] uppercase font-medium">{children}</span>
    <div className="w-8 h-px bg-navy-200" />
  </div>
);

const AboutUs: React.FC = () => {
  const values = [
    {
      icon: <Target className="w-7 h-7" />,
      title: "Research Quality First",
      description: "We continuously refine our AI models to deliver deeper, faster, and more comprehensive market research — processing 1000+ indicators per query so you don't have to."
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "Retail Investor Focus",
      description: "Built specifically for Indian retail investors, we understand your unique challenges and provide tailored educational resources."
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Transparent & Trustworthy",
      description: "No hidden agendas or market manipulation. We provide honest, data-driven insights to help you make informed decisions."
    },
    {
      icon: <Zap className="w-7 h-7" />,
      title: "Innovation Driven",
      description: "Constantly evolving our AI technology to stay ahead of market trends and provide cutting-edge investment intelligence."
    }
  ];

  const team = [
    {
      name: "Shantanu Bose",
      role: "IndianStockMarketGPT Creator & Engineer",
      description: "15+ years as an investor, entrepreneur, and advisor. Technology enthusiast and problem-solver with a track record of transforming ideas into impactful ventures.",
      image: "/avatars/shantanu-bose.jpg"
    },
  ];

  const achievements = [
    { number: "<90s", label: "Analysis Speed" },
    { number: "25K+", label: "Active Users" },
    { number: "1,000+", label: "Indicators per Query" },
    { number: "24/7", label: "Market Monitoring" },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SeoMeta title="About IndianStockMarketGPT — AI Research for Indian Investors" description="Who we are and how IndianStockMarketGPT uses AI to explain Indian stocks and markets in plain English. Educational research only — AI investigates, you decide." url="https://indianstockmarketgpt.com/about" />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center bg-navy-50 rounded-full px-4 py-2 mb-8 border border-navy-100">
            <Award className="w-4 h-4 mr-2 text-navy-500" />
            <span className="text-sm font-medium text-navy-600 tracking-wide">AI Research · Education · No Investment Advice</span>
          </div>

          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-serif text-slate-900 mb-6 leading-tight">
            About Indian Stock<br className="hidden md:block" /> Market GPT
          </h1>

          <p className="text-base md:text-lg text-slate-600 mb-4 max-w-3xl mx-auto leading-relaxed">
            Welcome to Indian Stock Market GPT — India's first AI-powered stock market assistant designed to empower retail investors,
            traders, and financial learners with smart, data-backed insights.
          </p>
        </div>
      </section>

      {/* ── Who We Are ───────────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionLabel>Our Story</SectionLabel>
              <h2 className="text-3xl md:text-4xl font-bold font-serif mb-6 text-slate-900">Who We Are</h2>
              <div className="space-y-5 text-slate-600 leading-relaxed text-sm">
                <p>
                  We are a team of tech-savvy market enthusiasts, AI developers, and finance learners passionate about
                  simplifying stock analysis in India. Our mission is to bridge the gap between advanced AI technology
                  and everyday stock market users — with zero hype, pure utility.
                </p>
                <p>
                  We rank <span className="text-navy-500 font-semibold">#1 on Google</span> for "Indian stock market GPT" —
                  a sign of trust and relevance in this emerging space.
                </p>
              </div>
            </div>
            <div>
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                <div className="grid grid-cols-2 gap-6">
                  {achievements.map((a) => (
                    <div key={a.label} className="text-center">
                      <div className="text-3xl font-bold font-serif text-navy-500 mb-1">{a.number}</div>
                      <div className="text-slate-500 text-sm">{a.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── What is ISMGPT ───────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Platform</SectionLabel>
            <NavyDivider />
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">What is Indian Stock Market GPT?</h2>
            <p className="text-base text-slate-600 max-w-3xl mx-auto leading-relaxed">
              A custom GPT model fine-tuned to understand the complexities of the Indian stock market.
              Whether you're tracking Nifty 50, exploring mid-cap gems, or decoding technical indicators — our GPT helps you with:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <BarChart2 className="w-6 h-6" />, title: 'Stock Summaries', sub: '& Sector Insights' },
              { icon: <BrainCircuit className="w-6 h-6" />, title: 'AI Research Summaries', sub: 'Based on Historical Market Data' },
              { icon: <LineChart className="w-6 h-6" />, title: 'Technical Analysis', sub: 'Fundamental + Technical Breakdowns' },
              { icon: <GraduationCap className="w-6 h-6" />, title: 'Learning Resources', sub: 'For Retail Investors' },
            ].map(({ icon, title, sub }) => (
              <div key={title} className="bg-white p-6 rounded-xl border border-slate-200 text-center hover:border-navy-300 hover:shadow-sm transition-all">
                <div className="flex justify-center mb-3 text-navy-500">{icon}</div>
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{title}</h3>
                <p className="text-slate-500 text-xs">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why We Built This ────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Mission</SectionLabel>
            <NavyDivider />
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">Why We Built This</h2>
            <p className="text-base text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Retail investors often face information overload, contradictory advice, and too much noise. We wanted to build something:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Timer className="w-7 h-7" />, title: 'Fast', desc: 'No signups, no dashboards — just straight-up stock answers.' },
              { icon: <Fingerprint className="w-7 h-7" />, title: 'Honest', desc: 'No tips, no manipulation — just AI\'s interpretation of the facts.' },
              { icon: <Layers className="w-7 h-7" />, title: 'Accessible', desc: 'Whether you\'re new or experienced, the GPT speaks your language.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="bg-white p-8 rounded-2xl border border-slate-200 text-center hover:border-navy-300 hover:shadow-sm transition-all">
                <div className="flex justify-center mb-4 text-navy-500">{icon}</div>
                <h3 className="text-lg font-semibold font-serif mb-3 text-slate-800">{title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What Makes Us Unique ─────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Differentiation</SectionLabel>
            <NavyDivider />
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">What Makes Us Unique</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: <MapPin className="w-5 h-5" />, title: 'India-Specific Knowledge', desc: 'Not trained on global markets alone' },
              { icon: <BrainCircuit className="w-5 h-5" />, title: 'Built by Developers & Traders', desc: 'Not marketers' },
              { icon: <MessagesSquare className="w-5 h-5" />, title: 'Real-Time Answers', desc: 'Using the latest in AI & NLP' },
              { icon: <BookMarked className="w-5 h-5" />, title: 'Educational & Ethical', desc: 'We help you learn, not gamble' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-navy-300 hover:shadow-sm transition-all">
                <div className="flex items-center mb-3">
                  <span className="text-navy-500 mr-3">{icon}</span>
                  <h3 className="text-base font-semibold text-slate-800 font-serif">{title}</h3>
                </div>
                <p className="text-slate-600 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What's Next ──────────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Roadmap</SectionLabel>
            <NavyDivider />
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">What's Next</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">We're actively working on:</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <CalendarRange className="w-6 h-6" />, title: 'Weekly AI-Curated', sub: 'Market Outlooks' },
              { icon: <MessageCircle className="w-6 h-6" />, title: 'Community Forum', sub: 'Free Access' },
              { icon: <BookMarked className="w-6 h-6" />, title: 'Free Educational', sub: 'Courses' },
              { icon: <Video className="w-6 h-6" />, title: 'More Frequent', sub: '1:1 Sessions' },
            ].map(({ icon, title, sub }) => (
              <div key={title} className="bg-white p-6 rounded-xl border border-slate-200 text-center hover:border-navy-300 hover:shadow-sm transition-all">
                <div className="flex justify-center mb-3 text-navy-500">{icon}</div>
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{title}</h3>
                <p className="text-slate-500 text-xs">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Values ───────────────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Principles</SectionLabel>
            <NavyDivider />
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">Our Values</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do and every decision we make.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-navy-300 hover:shadow-sm transition-all group">
                <div className="text-navy-500 mb-4 group-hover:text-navy-700 transition-colors">
                  {value.icon}
                </div>
                <h3 className="text-base font-semibold font-serif mb-3 text-slate-800">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team ─────────────────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionLabel>Team</SectionLabel>
            <NavyDivider />
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">We are Building the Team<br className="hidden md:block" /> — Meanwhile, Meet Me</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              The mind behind Indian Stock Market GPT, combining decades of experience in AI, finance, and technology.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="max-w-sm w-full">
              {team.map((member, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl border border-slate-200 text-center shadow-sm">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full mx-auto mb-6 border-2 border-slate-200 object-cover"
                    onError={e => {
                      const fallback = "https://media.licdn.com/dms/image/v2/C4E03AQFTf2xU5Rcx6w/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1615278921047?e=1757548800&v=beta&t=H948tsBkP7FE_oPK7u9KdSD2mNlO1LwITuwO3Ko8oNA";
                      if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                    }}
                  />
                  <h3 className="text-2xl font-semibold font-serif mb-3 text-slate-900">
                    {member.name === "Shantanu Bose" ? (
                      <a
                        href="https://www.linkedin.com/in/iamshantanubose"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy-500 hover:text-navy-700 transition-colors"
                      >
                        {member.name}
                      </a>
                    ) : member.name}
                  </h3>
                  <div className="text-navy-500 font-medium mb-4 text-sm tracking-wide">{member.role}</div>
                  <p className="text-slate-600 leading-relaxed text-sm">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Join CTA ─────────────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto text-center">
          <div className="rounded-3xl p-12 border border-slate-200 bg-white shadow-sm">
            <div className="flex justify-center mb-6">
              <div className="border border-navy-100 bg-navy-50 p-4 rounded-2xl">
                <TrendingUp className="w-8 h-8 text-navy-500" />
              </div>
            </div>
            <h2 className="text-xl md:text-3xl font-bold font-serif mb-4 text-slate-900">Join the Movement</h2>
            <p className="text-base text-slate-600 mb-8 leading-relaxed max-w-xl mx-auto">
              Thousands are discovering how AI can reshape their investing experience.<br />
              <span className="font-semibold text-slate-800">You can be one of them.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/ask"
                className="bg-navy-500 hover:bg-navy-600 text-white px-8 py-4 rounded-xl text-base font-semibold transition-all shadow-sm tracking-wide"
              >
                Start Researching Free
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
